/**
 * A row of four facts about the account: how often it orders, its credit,
 * its open balance, and when the rep last talked to it. Only order cadence
 * has data so far; the others show just a dash.
 */

import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { SalesInsightsAccountResponse } from "@onsemble/shared/types/sales-insights";
import { MISSING, dollars, rate } from "../../format";

type Channel = keyof SalesInsightsAccountResponse["channels"];

/** The channel with the most orders, or null if none were recorded. Ties go to the first listed. */
export function mainChannel(channels: SalesInsightsAccountResponse["channels"]): Channel | null {
  // Checked in this order, so a tie goes to web, then phone, then counter
  const order: Channel[] = ["web", "phone", "counter", "other"];
  let best: Channel | null = null;
  for (const c of order) {
    // Only a channel with orders can win
    if (channels[c] > 0 && (best === null || channels[c] > channels[best])) best = c;
  }
  return best;
}

/** One cell: a small title, a big value, and optionally a small line under it. */
function Fact({ title, value, sub }: { title: string; value: ReactNode; sub?: ReactNode }) {
  return (
    // Every cell after the first gets a divider on its left; on phones the
    // third cell starts a new row, so it doesn't
    <div className="py-3.5 pr-4 [&+&]:border-l [&+&]:border-si-line [&+&]:pl-4 max-sm:[&:nth-child(3)]:border-l-0 max-sm:[&:nth-child(3)]:pl-0">
      <div className="text-si-sm font-medium text-si-ink-2">{title}</div>
      <div className="mt-0.5 text-si-xl font-medium tracking-[-0.02em] tabular-nums">{value}</div>
      {sub && <div className="mt-0.5 text-si-sm text-si-ink-3">{sub}</div>}
    </div>
  );
}

export function KeyFacts({ account }: { account: SalesInsightsAccountResponse }) {
  const { t } = useTranslation();
  // Text keys for this section
  const k = (name: string) => `pages.pcInsights.account.facts.${name}`;
  const channel = mainChannel(account.channels);

  return (
    <div className="mt-8 grid grid-cols-4 border-t border-si-line max-sm:grid-cols-2">
      {/* How often they order, the average order, and how they usually order */}
      <Fact
        title={t(k("cadenceTitle"))}
        value={t(k("perMonth"), { rate: rate(account.ordersPerMonth) })}
        sub={
          <>
            {t(k("avgOrder"), { amount: dollars(account.averageOrder) })}
            {channel && ` · ${t(k("mostly"), { channel: t(k(channel)) })}`}
          </>
        }
      />
      {/* No data yet for these three: just a dash */}
      <Fact title={t(k("creditTitle"))} value={MISSING} />
      <Fact title={t(k("balanceTitle"))} value={MISSING} />
      <Fact title={t(k("lastContactTitle"))} value={MISSING} />
    </div>
  );
}
